/**
 * WB4.7 — Recoverability ladder SSOT (Low → Medium → High → Finance).
 * Maps tier → recoverability mode → UI affordance → handler-hook fallbacks.
 *
 * Rule-table `safety{}` entries take precedence; handler hooks apply when no rule match.
 */

import {
  buildActionSafety,
  type RecoverabilityMode,
  type WorkbenchActionSafety,
  type WorkbenchRiskTier,
} from './workbench-action-safety-policy';
import {
  BULK_STATE_CHANGE_HANDLER_REFS,
  FINANCE_COMPENSATING_PREFIX,
} from './workbench-bulk-handler-refs';
import {
  resolveActionSafetyByActionId,
  resolveActionSafetyByHandlerRef,
} from './workbench-action-safety-lookup';

export type WorkbenchRecoverabilityUiAffordance =
  | 'none'
  | 'toast_undo'
  | 'revision_restore_banner'
  | 'correction_request_cta'
  | 'compensating_restore_cta';

export type WorkbenchTimelineRecoverabilityMode =
  | 'none'
  | 'toast_undo'
  | 'revision_restore'
  | 'correction_flow'
  | 'reversal_transaction';

export type WorkbenchHandlerHookKind = 'prefix' | 'exact';

export interface WorkbenchHandlerHook {
  kind: WorkbenchHandlerHookKind;
  pattern: string;
}

export interface WorkbenchRecoverabilityLadderEntry {
  tier: WorkbenchRiskTier;
  recoverabilityMode: RecoverabilityMode;
  timelineMode: WorkbenchTimelineRecoverabilityMode;
  uiAffordance: WorkbenchRecoverabilityUiAffordance;
  undoTtlMs?: number;
  revisionTtlMs?: number;
  requiresPopconfirm: boolean;
  allowsToastUndo: boolean;
  allowsDirectRestore: boolean;
  handlerHooks: readonly WorkbenchHandlerHook[];
}

export { BULK_STATE_CHANGE_HANDLER_REFS, FINANCE_COMPENSATING_PREFIX };

export const WORKBENCH_RECOVERABILITY_LADDER: readonly WorkbenchRecoverabilityLadderEntry[] = [
  {
    tier: 'low',
    recoverabilityMode: 'toast_undo',
    timelineMode: 'toast_undo',
    uiAffordance: 'toast_undo',
    undoTtlMs: 8_000,
    requiresPopconfirm: false,
    allowsToastUndo: true,
    allowsDirectRestore: true,
    handlerHooks: [{ kind: 'prefix', pattern: 'inline:update:' }],
  },
  {
    tier: 'medium',
    recoverabilityMode: 'revision_restore',
    timelineMode: 'revision_restore',
    uiAffordance: 'revision_restore_banner',
    revisionTtlMs: 24 * 60 * 60 * 1000,
    requiresPopconfirm: false,
    allowsToastUndo: false,
    allowsDirectRestore: true,
    handlerHooks: [
      { kind: 'prefix', pattern: 'thread:advance:' },
      { kind: 'prefix', pattern: 'bulk:status:' },
    ],
  },
  {
    tier: 'high',
    recoverabilityMode: 'approval_correction',
    timelineMode: 'correction_flow',
    uiAffordance: 'correction_request_cta',
    revisionTtlMs: 24 * 60 * 60 * 1000,
    requiresPopconfirm: true,
    allowsToastUndo: false,
    allowsDirectRestore: false,
    handlerHooks: [
      { kind: 'exact', pattern: 'crud:delete' },
      { kind: 'exact', pattern: BULK_STATE_CHANGE_HANDLER_REFS.delete },
      { kind: 'prefix', pattern: 'flow:start:' },
    ],
  },
  {
    tier: 'finance',
    recoverabilityMode: 'compensating_transaction',
    timelineMode: 'reversal_transaction',
    uiAffordance: 'compensating_restore_cta',
    revisionTtlMs: 24 * 60 * 60 * 1000,
    requiresPopconfirm: true,
    allowsToastUndo: false,
    allowsDirectRestore: true,
    handlerHooks: [{ kind: 'prefix', pattern: FINANCE_COMPENSATING_PREFIX }],
  },
] as const;

function recoverabilityLadderEntryAt(index: number): WorkbenchRecoverabilityLadderEntry {
  const entry = WORKBENCH_RECOVERABILITY_LADDER[index];
  if (!entry) {
    throw new Error(`Missing recoverability ladder entry at index ${index}`);
  }
  return entry;
}

const LADDER_BY_TIER: Record<WorkbenchRiskTier, WorkbenchRecoverabilityLadderEntry> = {
  low: recoverabilityLadderEntryAt(0),
  medium: recoverabilityLadderEntryAt(1),
  high: recoverabilityLadderEntryAt(2),
  finance: recoverabilityLadderEntryAt(3),
};

export function getWorkbenchRecoverabilityLadderEntry(
  tier: WorkbenchRiskTier
): WorkbenchRecoverabilityLadderEntry {
  return LADDER_BY_TIER[tier];
}

function handlerHookMatches(handlerRef: string, hook: WorkbenchHandlerHook): boolean {
  if (hook.kind === 'exact') {
    return handlerRef === hook.pattern;
  }
  return handlerRef.startsWith(hook.pattern);
}

/** Handler-prefix fallback when action-rule-table has no `safety{}` entry. */
export function resolveTierFromHandlerHooks(handlerRef: string): WorkbenchRiskTier | undefined {
  for (let index = WORKBENCH_RECOVERABILITY_LADDER.length - 1; index >= 0; index -= 1) {
    const entry = WORKBENCH_RECOVERABILITY_LADDER[index];
    if (!entry) {
      continue;
    }
    if (entry.handlerHooks.some((hook) => handlerHookMatches(handlerRef, hook))) {
      return entry.tier;
    }
  }
  return undefined;
}

export function resolveTimelineRecoverabilityMode(
  tier: WorkbenchRiskTier,
  undoable = false
): WorkbenchTimelineRecoverabilityMode {
  if (!undoable) {
    return 'none';
  }
  return getWorkbenchRecoverabilityLadderEntry(tier).timelineMode;
}

export function isBulkStateChangeHandlerRef(handlerRef: string): boolean {
  return (
    handlerRef.startsWith('bulk:status:') || handlerRef === BULK_STATE_CHANGE_HANDLER_REFS.delete
  );
}

export function financeReversalHandlerRef(sourceVoucherId: string): string {
  return `${FINANCE_COMPENSATING_PREFIX}${sourceVoucherId}`;
}

export function parseFinanceReversalSourceVoucherId(handlerRef: string): string | null {
  if (!handlerRef.startsWith(FINANCE_COMPENSATING_PREFIX)) {
    return null;
  }
  const sourceVoucherId = handlerRef.slice(FINANCE_COMPENSATING_PREFIX.length).trim();
  return sourceVoucherId || null;
}

function confirmationForLadderEntry(
  entry: WorkbenchRecoverabilityLadderEntry
): WorkbenchActionSafety['confirmation'] {
  if (!entry.requiresPopconfirm) {
    return 'none';
  }
  return entry.tier === 'finance' ? 'confirm_with_reason' : 'confirm';
}

/** Builds execution policy from ladder tier defaults (handler-hook fallback path). */
export function buildRecoverabilityPolicyFromLadderTier(
  tier: WorkbenchRiskTier
): WorkbenchActionSafety {
  const entry = getWorkbenchRecoverabilityLadderEntry(tier);
  return buildActionSafety(tier, [], {
    recoverabilityMode: entry.recoverabilityMode,
    confirmation: confirmationForLadderEntry(entry),
  });
}

function resolveRuleTableSafety(
  actionId: string,
  handlerRef?: string
): WorkbenchActionSafety | undefined {
  const trimmedActionId = actionId.trim();
  if (trimmedActionId) {
    const byActionId = resolveActionSafetyByActionId(trimmedActionId);
    if (byActionId) {
      return byActionId;
    }
  }

  if (handlerRef) {
    return resolveActionSafetyByHandlerRef(handlerRef);
  }

  return undefined;
}

/**
 * Single entry — rule-table `safety{}` first, then ladder handler-hook fallback, then low default.
 */
export function resolveRecoverabilityFromPolicy(
  actionId: string,
  handlerRef?: string
): WorkbenchActionSafety {
  const fromRuleTable = resolveRuleTableSafety(actionId, handlerRef);
  if (fromRuleTable) {
    return fromRuleTable;
  }

  if (handlerRef) {
    const fallbackTier = resolveTierFromHandlerHooks(handlerRef);
    if (fallbackTier) {
      return buildRecoverabilityPolicyFromLadderTier(fallbackTier);
    }
  }

  return buildActionSafety('low', []);
}

/** Handler-only tier lookup — undefined when no rule-table or ladder hook match. */
export function resolveRecoverabilityTierFromPolicy(
  handlerRef: string
): WorkbenchRiskTier | undefined {
  const fromRuleTable = resolveRuleTableSafety('', handlerRef);
  if (fromRuleTable) {
    return fromRuleTable.riskTier;
  }
  return resolveTierFromHandlerHooks(handlerRef);
}
